'use client';

import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useMemo,
} from 'react';
import { IntlMessageFormat } from 'intl-messageformat';

import type { Validator } from '@personly/libs-types';
import validateValue from '@personly/libs-validators';

export type Range = { min: number; max: number };

type ValidatorsContextType = {
  validate: (handle: string, value: string) => boolean;
  requirement: (handle: string) => string;
  range: (handle: string) => Range;
};

export type ValidatorValue = string | Date | URL;

export { ValidatorsProvider, useValidators };

interface Props {
  children: ReactNode;
  locale: string;
  messages: Record<string, string>;
  validators: Map<string, Validator>;
}

const ValidatorsContext = createContext<ValidatorsContextType | null>(null);

const ValidatorsProvider = ({
  children,
  validators,
  messages,
  locale,
}: Props) => {
  const requirement = useCallback(
    (handle: string) => {
      const validator = validators.get(handle);
      if (!validator) return '';

      const requirements: ReactNode[] = [];

      if (validator.regex) {
        const requirement = new IntlMessageFormat(
          messages['VALIDATORS.REGEX'],
          locale
        ).format<ReactNode>();

        requirements.push(requirement);
      }

      if (validator.min) {
        const requirement = new IntlMessageFormat(
          messages['VALIDATORS.MIN'],
          locale
        ).format({ count: validator.min });

        requirements.push(requirement);
      }

      if (validator.max) {
        const requirement = new IntlMessageFormat(
          messages['VALIDATORS.MAX'],
          locale
        ).format({ count: validator.max });

        requirements.push(requirement);
      }

      if (validator.typeHandle === 'URL') {
        const requirement = new IntlMessageFormat(
          messages['VALIDATORS.TYPES.URL'],
          locale
        ).format<ReactNode>();

        requirements.push(requirement);
      }

      if (validator.values && validator.values.length > 0) {
        const requirement = new IntlMessageFormat(
          messages['VALIDATORS.VALUES'],
          locale
        ).format<ReactNode>({ values: validator.values.join(',') });

        requirements.push(requirement);
      }

      const conjunction = new IntlMessageFormat(
        messages['VALIDATORS.CONJUNCTION'],
        locale
      ).format<ReactNode>();

      return toSentence(requirements, conjunction);
    },
    [validators, messages, locale]
  );

  const validate = useCallback(
    (handle: string, value: string) => {
      const validator = validators.get(handle);
      if (!validator) return false;

      return validateValue(validator, value);
    },
    [validators]
  );

  const range = useCallback(
    (handle: string) => {
      const validator = validators.get(handle);
      if (!validator) return { min: 0, max: 0 };
      if (!validator.min && !validator.max) return { min: 0, max: 0 };

      if (validator.typeHandle === 'TEXT') {
        const min = validator.min || 0;
        const max = validator.max || 0;
        return { min, max };
      }

      if (validator.typeHandle === 'DATE') {
        const year = new Date().getUTCFullYear();
        const min = year + (validator.min || 0);
        const max = year + (validator.max || 0);
        return { min, max };
      }
      return { min: 0, max: 0 };
    },
    [validators]
  );

  const value = useMemo(
    () => ({ validate, requirement, range }),
    [validate, requirement, range]
  );

  return (
    <ValidatorsContext.Provider value={value}>
      {children}
    </ValidatorsContext.Provider>
  );
};

function useValidators() {
  const context = useContext(ValidatorsContext);
  if (!context) throw new Error('useValidators requires a context!');

  return context;
}

export function toSentence(requirements: ReactNode[], conjunction: ReactNode) {
  return requirements.reduce<string>((prev, next, i) => {
    if (i < requirements.length - 1) return prev.concat(`${next}, `);
    return prev
      .concat(conjunction!.toString())
      .concat(' ')
      .concat(next!.toString());
  }, '');
}
